'use client'
import { title, subtitle } from '@/components/primitives'
import { Button } from '@nextui-org/button'

import 'swiper/css'
import 'swiper/css/pagination'
import { Card } from '@nextui-org/card'
import { Input } from '@nextui-org/react'
import axios from 'axios'
import { use, useEffect, useState } from 'react'
import { z } from 'zod'
import ContactUs from './contact-us/page'
import { useTheme } from 'next-themes'
import EqualizerBackground from '@/components/EqualizerBackground'
import { motion } from 'framer-motion'
import Link from 'next/link'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@nextui-org/react'

import ArtistAnalytics from '@/components/ArtistAnalytics'
import VenueInsights from '@/components/VenueInsights'
import { useRouter } from 'next/navigation'

export default function Home() {
  const router = useRouter()
  const { theme, resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  const isDark = resolvedTheme === 'dark' // Use resolvedTheme instead of theme.theme
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure()
  const {
    isOpen: isArtistModalOpen,
    onOpen: onArtistModalOpen,
    onOpenChange: onArtistModalOpenChange,
    onClose: onArtistModalClose,
  } = useDisclosure()
  const [androidEmail, setAndroidEmail] = useState('')
  const [androidError, setAndroidError] = useState('')
  const [androidSuccess, setAndroidSuccess] = useState('')

  // Artist form state
  const [artistEmail, setArtistEmail] = useState('')
  const [artistError, setArtistError] = useState('')
  const [artistSuccess, setArtistSuccess] = useState('')

  const emailSchema = z.string().email({ message: 'Please enter a valid email address' })
  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return null // or a loading spinner
  }
  const handleAndroidSubmit = () => {
    const result = emailSchema.safeParse(androidEmail)
    if (!result.success) {
      setAndroidError(result.error.errors[0]?.message || 'Invalid email')
      setAndroidSuccess('')
    } else {
      // You can add your API call here if you want to collect Android emails
      setAndroidSuccess("Thanks! We'll notify you when Android is ready.")
      setAndroidError('')
      setAndroidEmail('')
    }
  }

  const handleArtistSubmit = async () => {
    // Basic validation
    if (!artistEmail.trim()) {
      setArtistError('Please enter your email')
      setArtistSuccess('')
      return
    }

    const emailResult = emailSchema.safeParse(artistEmail)
    if (!emailResult.success) {
      setArtistError(emailResult.error.errors[0]?.message || 'Invalid email')
      setArtistSuccess('')
      return
    }

    try {
      const response = await fetch('/api/join-list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: artistEmail }),
      })

      const data = await response.json()

      if (response.ok) {
        setArtistSuccess("Thanks for your interest! We'll be in touch soon.")
        setArtistError('')
        setArtistEmail('')

        // Close modal after a short delay
        setTimeout(() => {
          onArtistModalClose()
          setArtistSuccess('')
        }, 2000)
      } else {
        setArtistError(data.message || 'Something went wrong. Please try again.')
        setArtistSuccess('')
      }
    } catch (error) {
      setArtistError('Network error. Please try again.')
      setArtistSuccess('')
    }
  }

  const gridItems = [
    {
      icon: '/playbutton.svg',
      title: 'Post clips',
      description: 'Easily share your favorite videos from concerts with your friends.',
    },
    {
      icon: '/search.svg',
      title: 'Search for artists',
      description: 'Find your favorite artists and shows, and see their latest clips.',
    },
    {
      icon: '/chat.svg',
      title: 'Chat with other fans',
      description: 'Chat with people in your local scene, or around the world.',
    },
    {
      icon: '/hq.svg',
      title: 'Find high quality content',
      description: "It's not 1999, we don't need low quality videos anymore.",
    },
    {
      icon: '/pencil.svg',
      title: "Can't find your favorite artist?",
      description:
        "The power is in your hands. Upload a clip and create the artist. There's room for everyone.",
    },
    {
      icon: '/envelope.svg',
      title: 'Share content with friends',
      description: 'Share your favorite moments with friends instantly.',
    },
  ]

  return (
    <>
      <EqualizerBackground />
      <main role="main" aria-label="LLoclip Homepage">
        <section
          id="home"
          className="items-center justify-center flex border-0 border-slate-600 flex-col gap-5"
          aria-labelledby="hero-heading"
        >
          {/* Hero Section */}
          <div className="hero-section" role="banner">
            <h1 id="hero-heading" className="sr-only">
              LLoclip - The next generation live music platform
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="relative border-0 border-red-600 w-full flex flex-col lg:flex-row items-center justify-center gap-8"
            >
              <div className="w-full lg:w-1/2 flex-shrink-0 flex-grow-0">
                <div
                  className={
                    `max-w-4xl w-full mx-auto px-8 py-6 sm:px-14 sm:py-8 lg:px-16 lg:py-10 ` +
                    `rounded-2xl shadow-2xl ` +
                    `backdrop-blur-lg ` +
                    (isDark
                      ? 'bg-black/30 border border-white/20'
                      : 'bg-white/30 border border-black/10')
                  }
                  style={{ zIndex: 1 }}
                >
                  <div className="text-center justify-center border-0 border-yellow-600 ">
                    <motion.span
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.7, ease: 'easeOut' }}
                      className={title({ darkBackground: isDark, size: 'lg' })}
                      style={{ display: 'inline-block' }}
                    >
                      The next&nbsp;
                    </motion.span>
                    <motion.span
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
                      className={title({ color: 'blue', size: 'lg' })}
                      style={{ display: 'inline-block' }}
                    >
                      generation&nbsp;
                    </motion.span>
                    <br />
                    <motion.span
                      initial={{ opacity: 0, y: 20, rotate: 0 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        rotate: [0, -1, 1, 0],
                      }}
                      transition={{
                        delay: 0.4,
                        duration: 1.2,
                        ease: 'easeInOut',
                      }}
                      className={title({ darkBackground: isDark })}
                      style={{ display: 'inline-block' }}
                      whileHover={{ scale: 1.03 }}
                    >
                      live music platform
                    </motion.span>
                    <motion.div
                      className={subtitle({ class: 'mt-4' })}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.7, ease: 'easeOut' }}
                    >
                      Easy access to your favorite artists, live music moments, and more.
                    </motion.div>
                    <div className="flex flex-col gap-4 mt-6 w-full max-w-md mx-auto">
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.8, duration: 0.5, type: 'spring' }}
                      >
                        <Link href="/join-beta">
                          <motion.div
                            className="w-full h-12 bg-gradient-to-r from-primary to-primary-200 text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 cursor-pointer"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            Join the Beta
                            <img src="/clipboard.svg" alt="clipboard" className="w-5 h-5" />
                          </motion.div>
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-1/2 border-0 border-pink-600 relative flex items-center justify-center mt-8 lg:mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.8, ease: 'easeOut' }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <motion.img
                    src="/guitar.svg"
                    alt="Guitar icon representing live music and concerts"
                    className="absolute opacity-100 w-3/4 h-3/4 top-0 right-0"
                    initial={{ y: 0 }}
                    animate={{ y: [0, -10, 0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
                  />
                  <motion.img
                    src="/bombsvg.svg"
                    alt="Explosive energy icon symbolizing live music excitement"
                    className="absolute opacity-100 w-3/4 h-1/12 bottom-0 "
                    initial={{ y: 0 }}
                    animate={{ y: [0, 8, 0, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 7, ease: 'easeInOut' }}
                  />
                  <motion.img
                    src="/app-screenshots-2/profile2.png"
                    alt="LLoclip mobile app screenshot showing user profile interface"
                    className="relative w-3/4 h-3/4"
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.8, ease: 'easeOut' }}
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Add this after your hero section */}
          <nav aria-label="Page navigation" className="hidden">
            <ul>
              <li>
                <a href="#features">Features</a>
              </li>
              <li>
                <a href="#about">About</a>
              </li>
              <li>
                <a href="#artist-analytics">Artist Analytics</a>
              </li>
              <li>
                <a href="#venue-insights">Venue Insights</a>
              </li>
            </ul>
          </nav>

          {/* Mission Section */}
          <section
            aria-labelledby="mission-heading"
            className="mission-section relative z-10 px-4 sm:px-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="text-center"
            >
              <h2
                id="mission-heading"
                className="text-center font-bold text-3xl sm:text-4xl md:text-5xl mb-4 px-4"
              >
                Our Mission
              </h2>
              <p className="text-center text-base sm:text-lg md:text-xl max-w-2xl mb-6 mx-auto px-4">
                At Lloclip, we believe live music is more than just sound—it's a shared experience
                that connects people across the world. Our mission is to empower fans and artists to
                capture, share, and relive the magic of live performances, building a vibrant
                community where every moment matters.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <span className="px-4 sm:px-6 py-2 rounded-full bg-primary text-white font-semibold shadow-lg text-sm sm:text-base">
                  Community
                </span>
                <span className="px-4 sm:px-6 py-2 rounded-full bg-secondary text-white font-semibold shadow-lg text-sm sm:text-base">
                  Creativity
                </span>
                <span className="px-4 sm:px-6 py-2 rounded-full bg-gradient-to-r from-primary to-primary-200 text-white font-semibold shadow-lg text-sm sm:text-base">
                  Connection
                </span>
              </div>
            </motion.div>
          </section>

          {/* Features Section */}
          <section id="features" aria-labelledby="features-heading" className="features-section">
            <h2 id="features-heading" className="text-center font-bold text-4xl">
              Full featured live music sharing platform
            </h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: 0.2, duration: 0.7, ease: 'easeOut' }}
              className={`text-center text-xl ${isDark ? 'text-secondary' : 'text-secondary'} ${isDark ? 'text-white/80' : 'text-black/80'}`}
            >
              Search for your favorite artists, find your favorite shows, and share your favorite
              clips.
            </motion.p>
            <div className="rounded-2xl bg-transparent p-3 w-fit shadow-2xl border border-gray-300/20 shadow-black/50 mt-8 mx-auto">
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.13,
                    },
                  },
                }}
              >
                {gridItems.map((item, idx) => (
                  <motion.div
                    key={item.title}
                    variants={{
                      hidden: { opacity: 0, y: 40 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                    whileHover={{ scale: 1.06, boxShadow: '0 8px 32px 0 rgba(36,133,236,0.18)' }}
                  >
                    <Card
                      isFooterBlurred
                      radius="lg"
                      className={`border-0 flex flex-col items-center justify-center p-2 shadow-none w-full max-w-sm transition-all ${isDark ? 'bg-white/10' : 'bg-white/80'}`}
                    >
                      <div className="flex items-center justify-center p-2 rounded-lg bg-gradient-to-r from-primary to-primary/70 border-0 ">
                        <img src={item.icon} alt="Play button" className="w-14 h-14" />
                      </div>
                      <p
                        className={`text-large capitalize font-bold mt-2 ${isDark ? 'text-white/90' : 'text-black/90'}`}
                      >
                        {item.title}
                      </p>
                      <p
                        className={`text-sm text-center w-full px-4 ${isDark ? 'text-white/70' : 'text-black/70'}`}
                      >
                        {item.description}
                      </p>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* About Section */}
          <section id="about" aria-labelledby="about-heading" className="about-section">
            <h2 id="about-heading" className="sr-only">
              About LLoclip
            </h2>
            <div className="relative w-full flex items-center justify-center mt-24">
              {/* Blurred Gradient Background */}
              <svg
                className="absolute -z-10 left-1/2 top-0 -translate-x-1/2"
                width="900"
                height="340"
                viewBox="0 0 900 340"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ filter: 'blur(60px)' }}
              >
                <ellipse cx="450" cy="170" rx="400" ry="120" fill="#2485EC" fillOpacity="0.13" />
                <ellipse cx="600" cy="200" rx="200" ry="80" fill="#FEC941" fillOpacity="0.10" />
                <ellipse cx="300" cy="120" rx="180" ry="60" fill="#ff00c8" fillOpacity="0.08" />
              </svg>
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className={`w-full flex flex-col lg:flex-row items-center justify-between gap-8 ${isDark ? 'text-white' : 'text-black'}`}
                id="about"
              >
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: 0.1, duration: 0.7, ease: 'easeOut' }}
                  className="w-full lg:w-1/2 p-5 lg:p-10"
                >
                  {/*
                    // PSEUDOCODE: Replace this with a video player in the future
                    <video
                      src="/path-to-your-video.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="rounded-2xl shadow-lg w-full h-auto object-cover"
                    />
                  */}
                  <div className="relative flex items-center justify-center border-0 border-blue-700 p-5">
                    <img
                      src="/sound.svg"
                      alt="Guitar background"
                      className="absolute opacity-100 w-2/4 h-3/4 top-0 right-0"
                    />
                    <img
                      src="/bombsvg.svg"
                      alt="img"
                      className="absolute opacity-100 w-2/4 h-1/12 bottom-0"
                    />
                    <img
                      src="/app-screenshots-2/grinns.png"
                      alt="img"
                      className="relative w-1/2 sm:w-1/2 h-1/12"
                    />
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: 0.2, duration: 0.7, ease: 'easeOut' }}
                  className="w-full lg:w-1/2 p-5 lg:p-10"
                >
                  <div className="wow fadeInUp lg:max-w-[510px] lg:ml-auto">
                    <span className="mb-4 block text-lg font-medium text-primary md:text-[22px]">
                      Watch Clips
                    </span>
                    <h2
                      className={`mb-4 text-2xl sm:text-3xl font-bold md:text-4xl md:leading-tight ${isDark ? 'text-white' : 'text-black'}`}
                    >
                      Watch Your Favorite Clips
                    </h2>
                    <p
                      className={`mb-[30px] text-base leading-relaxed ${isDark ? 'text-white/80' : 'text-black/80'}`}
                    >
                      We no longer have to depend on low quality videos from other sites. Now we can
                      leverage the best quality videos from our phones.
                    </p>
                    <motion.div
                      className="mb-[30px] flex flex-col sm:flex-row items-start sm:items-center"
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: 0.3, duration: 0.6, ease: 'easeOut' }}
                    >
                      <div
                        className={`mb-4 sm:mb-0 mr-0 sm:mr-[22px] flex h-[60px] w-[60px] items-center justify-center rounded-full border border-stroke text-xl font-semibold ${isDark ? 'text-white bg-white/10 border-white/30' : 'text-black bg-black/10 border-black/20'}`}
                      >
                        01
                      </div>
                      <div>
                        <h5
                          className={`text-xl font-medium ${isDark ? 'text-white' : 'text-black'}`}
                        >
                          High Quality Content
                        </h5>
                        <p className={`text-base ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Share your concert experiences in crystal clear quality.
                        </p>
                      </div>
                    </motion.div>
                    <motion.div
                      className="flex flex-col sm:flex-row items-start sm:items-center"
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: 0.4, duration: 0.6, ease: 'easeOut' }}
                    >
                      <div
                        className={`mb-4 sm:mb-0 mr-0 sm:mr-[22px] flex h-[60px] w-[60px] items-center justify-center rounded-full border border-stroke text-xl font-semibold ${isDark ? 'text-white bg-white/10 border-white/30' : 'text-black bg-black/10 border-black/20'}`}
                      >
                        02
                      </div>
                      <div>
                        <h5
                          className={`text-xl font-medium ${isDark ? 'text-white' : 'text-black'}`}
                        >
                          Easy Sharing
                        </h5>
                        <p className={`text-base ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Share your favorite moments with friends instantly.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* First class Searching Section */}
          <div className="relative w-full flex flex-col lg:flex-row items-center justify-between mt-24 px-4 sm:px-6">
            <div className="border-0 border-blue-700 w-full lg:w-1/2 p-5 lg:p-10">
              <div className="wow fadeInUp lg:max-w-[510px] lg:ml-auto">
                <span className="mb-4 block text-lg font-medium text-primary md:text-[22px]">
                  Find what you need
                </span>
                <h2 className="mb-4 capitalize text-2xl sm:text-3xl font-bold text-black dark:text-white md:text-4xl md:text-[44px] md:leading-tight">
                  First class Searching
                </h2>
                <p className="mb-[30px] text-base leading-relaxed text-black dark:text-white/80">
                  When you upload a clip onto lloclip, you provide critical metadata that allows you
                  and other community members to find it later. We leverage this metadata to provide
                  a first class searching experience, allowing you to find the exact clip you're
                  looking for.
                </p>
              </div>
            </div>

            <div className="border-0 border-yellow-800 w-full lg:w-1/2 p-5 lg:p-10">
              <div className="relative flex items-center justify-center border-0 border-blue-700 p-5">
                {/* Animated Blob Background - Different Style */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    className="w-80 h-80 rounded-full bg-gradient-to-r from-primary/25 to-secondary/15 blur-3xl"
                    animate={{
                      scale: [1, 1.3, 0.7, 1.4, 1],
                      rotate: [0, 45, 90, 135, 180, 225, 270, 315, 360],
                      borderRadius: [
                        '50%',
                        '40% 60% 30% 70%',
                        '60% 40% 50% 50%',
                        '30% 60% 70% 40%',
                        '50% 40% 30% 60%',
                        '50%',
                      ],
                      x: [0, 20, -15, 25, -10, 0],
                      y: [0, -15, 20, -25, 15, 0],
                    }}
                    transition={{
                      duration: 15,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                  <motion.div
                    className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-secondary/20 to-primary/10 blur-2xl"
                    animate={{
                      scale: [0.9, 1.2, 0.8, 1.1, 0.9],
                      rotate: [360, 315, 270, 225, 180, 135, 90, 45, 0],
                      borderRadius: [
                        '60% 40% 30% 70%',
                        '50% 60% 40% 50%',
                        '30% 60% 70% 40%',
                        '50% 40% 30% 60%',
                        '40% 60% 30% 70%',
                      ],
                      x: [0, -25, 15, -20, 30, 0],
                      y: [0, 20, -25, 15, -20, 0],
                    }}
                    transition={{
                      duration: 18,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 2,
                    }}
                  />
                  <motion.div
                    className="absolute w-56 h-56 rounded-full bg-gradient-to-r from-primary/15 to-secondary/20 blur-xl"
                    animate={{
                      scale: [1.1, 0.8, 1.3, 0.9, 1.1],
                      rotate: [180, 225, 270, 315, 360, 45, 90, 135, 180],
                      borderRadius: [
                        '50% 40% 30% 60%',
                        '30% 60% 70% 40%',
                        '60% 40% 50% 50%',
                        '40% 60% 30% 70%',
                        '50% 40% 30% 60%',
                      ],
                      x: [0, 30, -20, 25, -15, 0],
                      y: [0, -20, 25, -30, 20, 0],
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 4,
                    }}
                  />
                  <motion.div
                    className="absolute w-40 h-40 rounded-full bg-gradient-to-r from-secondary/10 to-primary/15 blur-lg"
                    animate={{
                      scale: [0.8, 1.1, 0.9, 1.2, 0.8],
                      rotate: [270, 315, 360, 45, 90, 135, 180, 225, 270],
                      borderRadius: [
                        '30% 60% 70% 40%',
                        '50% 40% 30% 60%',
                        '60% 40% 50% 50%',
                        '40% 60% 30% 70%',
                        '30% 60% 70% 40%',
                      ],
                      x: [0, -15, 20, -25, 15, 0],
                      y: [0, 25, -30, 20, -15, 0],
                    }}
                    transition={{
                      duration: 22,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 6,
                    }}
                  />
                </div>
                <img
                  src="/app-screenshots-2/search-2.png"
                  alt="App screenshot 2"
                  className="relative w-2/3 sm:w-1/2 -rotate-12 z-10 shadow-lg"
                />
                <img
                  src="/app-screenshots-2/search.png"
                  alt="App screenshot 1"
                  className="absolute w-2/3 sm:w-1/2 rotate-12 translate-x-12 sm:translate-x-24 translate-y-2 sm:translate-y-5 shadow-lg z-10"
                />
              </div>
            </div>
          </div>

          {/* Power to the people Section */}
          <div className="relative w-full flex flex-col lg:flex-row items-center justify-between mt-24 px-4 sm:px-6">
            <div className="border-0 border-yellow-800 w-full lg:w-1/2 p-5 lg:p-10">
              <div className="relative flex items-center justify-center border-0 border-blue-700 p-5">
                {/* Animated Blob Background */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    className="w-96 h-96 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 blur-3xl"
                    animate={{
                      scale: [1, 1.2, 0.8, 1.1, 1],
                      rotate: [0, 90, 180, 270, 360],
                      borderRadius: [
                        '50%',
                        '60% 40% 30% 70%',
                        '30% 60% 70% 40%',
                        '50% 60% 30% 60%',
                        '50%',
                      ],
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                  <motion.div
                    className="absolute w-80 h-80 rounded-full bg-gradient-to-r from-secondary/15 to-primary/15 blur-2xl"
                    animate={{
                      scale: [0.8, 1.1, 0.9, 1.2, 0.8],
                      rotate: [360, 270, 180, 90, 0],
                      borderRadius: [
                        '30% 60% 70% 40%',
                        '50% 60% 30% 60%',
                        '60% 40% 30% 70%',
                        '50%',
                        '30% 60% 70% 40%',
                      ],
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 1,
                    }}
                  />
                  <motion.div
                    className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 blur-xl"
                    animate={{
                      scale: [1.2, 0.8, 1.1, 0.9, 1.2],
                      rotate: [180, 360, 270, 180, 90],
                      borderRadius: [
                        '50% 60% 30% 60%',
                        '60% 40% 30% 70%',
                        '30% 60% 70% 40%',
                        '50%',
                        '60% 40% 30% 70%',
                      ],
                    }}
                    transition={{
                      duration: 12,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 2,
                    }}
                  />
                </div>
                <img
                  src="/app-screenshots-2/grinns-profile2.png"
                  alt="Community-driven platform"
                  className="relative w-2/3 sm:w-1/2 shadow-lg z-10"
                />
              </div>
            </div>

            <div className="border-0 border-blue-700 w-full lg:w-1/2 p-5 lg:p-10">
              <div className="wow fadeInUp lg:max-w-[510px] lg:ml-auto">
                <span className="mb-4 block text-lg font-medium text-primary md:text-[22px]">
                  Community driven
                </span>
                <h2 className="mb-4 capitalize text-2xl sm:text-3xl font-bold text-black dark:text-white md:text-4xl md:text-[44px] md:leading-tight">
                  Power to the people
                </h2>
                <p className="mb-[30px] text-base leading-relaxed text-black dark:text-white/80">
                  Lloclip is built by the community, for the community. Every video you upload
                  contributes to an artist's "lore" - creating a rich tapestry of live music moments
                  that tell the story of their journey. Your uploads don't just share a moment; they
                  become part of a collective memory that grows with every contribution.
                </p>
              </div>
            </div>
          </div>

          {/* Artist Analytics Section */}
          <ArtistAnalytics onArtistModalOpen={onArtistModalOpen} />

          {/* Venue Insights Section */}
          <VenueInsights />
        </section>
      </main>

      <div className="rounded-2xl bg-white/30 dark:bg-black/30 border border-black/10 dark:border-white/20 px-10 pt-14 shadow-card sm:px-20 lg:px-12 lg:pt-20 xl:px-20 w-full">
        <div className="-mx-4 flex flex-wrap">
          <div className="w-full self-center px-4 lg:w-1/2">
            <div className="wow fadeInUp mx-auto max-w-[530px] text-center lg:ml-0 lg:text-left">
              <h2 className="mb-4 text-3xl font-bold text-black dark:text-white sm:text-4xl md:text-[38px] md:leading-tight">
                Join the Waitlist & Get Early Access{' '}
              </h2>
              <p className="mb-10 text-base text-black dark:text-white/80">
                Be among the first to experience the next generation of concert videos. Sign up now
                to get notified when we launch.
              </p>
              <div className="flex flex-col gap-4 max-w-md">
                {/* <Input
                  label="Email"
                  placeholder="Enter your email to join the waitlist"
                  variant="bordered"
                  size="lg"
                  value={androidEmail}
                  onChange={(e) => setAndroidEmail(e.target.value)}
                  classNames={{
                    input: "bg-transparent",
                    inputWrapper: "bg-transparent border-2",
                  }}
                /> */}
                {androidError && <p className="text-red-500">{androidError}</p>}
                {androidSuccess && <p className="text-green-500">{androidSuccess}</p>}
                <Button
                  onPress={() => router.push('/join-beta')}
                  className="w-full h-12 bg-gradient-to-r from-primary to-primary/70 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  Join the beta
                  <img src="/clipboard.svg" alt="clipboard" className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          <div className="w-full px-4 lg:w-1/2">
            <div className="wow fadeInUp relative z-10 mx-auto mt-20 max-w-[435px] lg:mt-0">
              <img
                alt="App preview"
                src="/app-screenshots-2/profile-with-edit-button.png"
                className="mx-auto max-w-full rounded-lg shadow-xl"
              />
              <div className="absolute -top-5 left-0 right-0 -z-10 aspect-square w-full rounded-full bg-gradient-to-r from-primary/30 to-primary/70 blur-xl"></div>

              {/* Decorative elements */}
              <div className="absolute right-0 top-0 -z-10">
                <svg
                  width="60"
                  height="43"
                  viewBox="0 0 60 43"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* ... SVG paths ... */}
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactUs />

      {/* Android Modal */}
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Android Beta Coming Soon</ModalHeader>
              <ModalBody>
                <p>
                  We're not ready for Android yet, but leave your email and we'll notify you when
                  it's available!
                </p>
                <Input
                  label="Email"
                  placeholder="Enter your email"
                  variant="bordered"
                  size="lg"
                  value={androidEmail}
                  onChange={(e) => setAndroidEmail(e.target.value)}
                  classNames={{
                    input: 'bg-transparent',
                    inputWrapper: 'bg-transparent border-2',
                  }}
                />
                {androidError && <p className="text-red-500">{androidError}</p>}
                {androidSuccess && <p className="text-green-500">{androidSuccess}</p>}
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onClick={handleAndroidSubmit}>
                  Notify Me
                </Button>
                <Button variant="light" onClick={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Artist Contact Modal */}
      <Modal
        isOpen={isArtistModalOpen}
        onOpenChange={onArtistModalOpenChange}
        onClose={onArtistModalClose}
        size="lg"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Artist Partnership Inquiry</ModalHeader>
              <ModalBody className="space-y-4">
                <p className="text-base">
                  Interested in learning more about our artist analytics and partnership
                  opportunities? Get in touch with us!
                </p>

                <Input
                  label="Email"
                  placeholder="Your email address"
                  variant="bordered"
                  size="lg"
                  value={artistEmail}
                  onChange={(e) => setArtistEmail(e.target.value)}
                  classNames={{
                    input: 'bg-transparent',
                    inputWrapper: 'bg-transparent border-2',
                  }}
                />

                {artistError && <p className="text-red-500 text-sm">{artistError}</p>}
                {artistSuccess && <p className="text-green-500 text-sm">{artistSuccess}</p>}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleArtistSubmit}
                  disabled={artistSuccess !== ''}
                >
                  Send Message
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
